import { Modal } from '@material-ui/core';
import { DataGrid, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarDensitySelector, GridToolbarExport, GridToolbarFilterButton } from '@material-ui/data-grid';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { configs, formatCurrency } from '../../../assets/Config';
import '../../../assets/styles/RM.css'
import '../../../assets/styles/NM.css'
import { addTopMoverPriceReason, getTopMovers } from '../../../services/apis/companyManagement/CMApi';
import { useLogin } from '../../../services/contexts/AdminLoginContext';
import LeftBarPage from '../../includes/dashboard/LeftBarPage';
import TopBarPage from '../../includes/dashboard/TopBarPage';
import ToastPage from '../../includes/ToastPage';

const DAYS_OPTIONS = [
    { value: "7", label: "7 Days" },
    { value: "30", label: "30 Days" },
    { value: "365", label: "365 Days" }
]

function reasonKey(days) {
    return `${days}_day_price_movement_reason`
}

function decodeHtml(text) {
    if (!text) return ""
    return String(text).replace(/&amp;/g, "&")
}

function formatPercent(value) {
    if (value === null || value === undefined || value === "") return ""
    const num = parseFloat(value)
    if (isNaN(num)) return value
    return `${num.toFixed(2)}%`
}

function mapRows(list) {
    return (list || []).map((item, index) => ({
        ...item,
        id: item.id || index,
        company_id: item.id,
        name: item.name || "",
        sector: decodeHtml(item.sector),
        "7_day_price_movement_reason": item["7_day_price_movement_reason"] || "",
        "30_day_price_movement_reason": item["30_day_price_movement_reason"] || "",
        "365_day_price_movement_reason": item["365_day_price_movement_reason"] || ""
    }))
}

function extractPayload(payload) {
    const data = payload && (payload.gainers || payload.loosers || payload.losers) ? payload : (payload && payload.data) || payload || {}
    return {
        gainers: Array.isArray(data.gainers) ? data.gainers : [],
        loosers: Array.isArray(data.loosers) ? data.loosers : (Array.isArray(data.losers) ? data.losers : [])
    }
}

export default function TopMoversReasonPage() {

    const { setCurrModule, setShowSuccess, allowedFunc, token, logout, setLoginError } = useLogin()
    const history = useHistory()

    const [days, setDays] = useState("7")
    const [gainers, setGainers] = useState([])
    const [loosers, setLoosers] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState("")
    const [reasonModal, setReasonModal] = useState(false)
    const [selectedCompany, setSelectedCompany] = useState(null)
    const [reason, setReason] = useState("")
    const [formError, setFormError] = useState("")

    useEffect(() => {
        try {
            if (allowedFunc.includes(configs.functionList['company_management:view_company'])) {
                setCurrModule(configs.modules.topMoversReason)
                fetchTopMovers(days)
            } else {
                return history.goBack()
            }
        } catch (err) {
            console.error(err)
        }
    }, [days])

    async function fetchTopMovers(selectedDays) {
        try {
            setLoading(true)
            setError("")
            let response = await getTopMovers(token, selectedDays)
            if (response && response.status === 200) {
                const payload = extractPayload(response.data)
                setGainers(mapRows(payload.gainers))
                setLoosers(mapRows(payload.loosers))
            } else if (response && response.status === 401) {
                logout()
                setLoginError(configs.unauthorizedErrorMessage)
            } else {
                setGainers([])
                setLoosers([])
                setError(response && response.data && response.data.error ? response.data.error : "Unable to fetch top movers.")
            }
        } catch (err) {
            console.error(err)
            setGainers([])
            setLoosers([])
            setError("Unable to fetch top movers.")
        } finally {
            setLoading(false)
        }
    }

    function openReasonModal(row) {
        setSelectedCompany(row)
        setReason(row[reasonKey(days)] || "")
        setFormError("")
        setReasonModal(true)
    }

    function handleClose() {
        setReasonModal(false)
        setSelectedCompany(null)
        setReason("")
        setFormError("")
    }

    function getReasonPayload() {
        return {
            sevenDay: days === "7" ? reason : (selectedCompany && selectedCompany["7_day_price_movement_reason"] || ""),
            thirtyDay: days === "30" ? reason : (selectedCompany && selectedCompany["30_day_price_movement_reason"] || ""),
            year: days === "365" ? reason : (selectedCompany && selectedCompany["365_day_price_movement_reason"] || "")
        }
    }

    function updateReasonInList(list, companyId) {
        const payload = getReasonPayload()
        return (list || []).map((item) => {
            if (`${item.company_id}` === `${companyId}`) {
                return {
                    ...item,
                    "7_day_price_movement_reason": payload.sevenDay,
                    "30_day_price_movement_reason": payload.thirtyDay,
                    "365_day_price_movement_reason": payload.year
                }
            }
            return item
        })
    }

    async function saveReason() {
        try {
            if (!selectedCompany || !selectedCompany.company_id) {
                setFormError("Please select a valid company.")
                return
            }

            document.getElementById("addReasonBtn").classList.toggle("d-none")
            document.getElementById("addReasonBtnLoader").classList.toggle("d-none")

            const payload = getReasonPayload()
            let response = await addTopMoverPriceReason(token, selectedCompany.company_id, payload.sevenDay, payload.thirtyDay, payload.year)
            if (response && response.status === 200) {
                setGainers((prev) => updateReasonInList(prev, selectedCompany.company_id))
                setLoosers((prev) => updateReasonInList(prev, selectedCompany.company_id))
                handleClose()
                setShowSuccess(true)
            } else if (response && response.status === 401) {
                logout()
                setLoginError(configs.unauthorizedErrorMessage)
            } else {
                setFormError(response && response.data && response.data.error ? response.data.error : "Unable to save reason.")
            }
        } catch (err) {
            console.error(err)
            setFormError("Unable to save reason.")
        } finally {
            const btn = document.getElementById("addReasonBtn")
            const loader = document.getElementById("addReasonBtnLoader")
            if (btn) btn.classList.remove("d-none")
            if (loader) loader.classList.add("d-none")
        }
    }

    const columns = [
        { field: "name", headerName: "Company Name", minWidth: 240, flex: 0.8, cellClassName: "break-text" },
        {
            field: "buy_display_price", headerName: "Current Price", minWidth: 130, flex: 0.35, cellClassName: "break-text",
            renderCell: (params) => formatCurrency(parseFloat(params.row.buy_display_price)),
            valueGetter: (params) => params.row.buy_display_price
        },
        {
            field: "buy_display_price_days_ago", headerName: `${days} Day Ago Price`, minWidth: 140, flex: 0.35, cellClassName: "break-text",
            renderCell: (params) => formatCurrency(parseFloat(params.row.buy_display_price_days_ago)),
            valueGetter: (params) => params.row.buy_display_price_days_ago
        },
        {
            field: "price_change_percentage", headerName: "Price Change %", minWidth: 140, flex: 0.35,
            renderCell: (params) => {
                const value = parseFloat(params.row.price_change_percentage)
                const colorClass = isNaN(value) ? "" : (value > 0 ? "text-success" : (value < 0 ? "text-danger" : ""))
                return <span className={colorClass}>{formatPercent(params.row.price_change_percentage)}</span>
            },
            valueGetter: (params) => params.row.price_change_percentage
        },
        {
            field: "reason", headerName: `${days} Day Reason`, minWidth: 180, flex: 0.6, cellClassName: "break-text",
            valueGetter: (params) => params.row[reasonKey(days)] || ""
        },
        {
            field: "add_reason", headerName: " ", minWidth: 110, flex: 0.25, sortable: false, filterable: false, cellClassName: "text-primary", renderCell: (params) => {
                const hasReason = params.row[reasonKey(days)]
                return <a href="#" className="custom-link" onClick={(e) => {
                    e.preventDefault()
                    openReasonModal(params.row)
                }}>{hasReason ? "Edit Reason" : "Add Reason"}</a>
            }
        }
    ]

    function CustomToolbar() {
        return <GridToolbarContainer>
            <GridToolbarColumnsButton />
            <GridToolbarFilterButton />
            <GridToolbarDensitySelector />
            <GridToolbarExport csvOptions={{ allColumns: true }} />
        </GridToolbarContainer>
    }

    function renderTable(title, rows) {
        return (
            <div className="mb-3">
                <div className="ml-3 mb-1">
                    <p className="h5 text-custom-grey mb-0">{title}</p>
                </div>
                <div className="m-2" style={{ height: "360px", width: "97%" }}>
                    <DataGrid
                        rows={rows}
                        getRowId={(row) => row.id}
                        headerHeight={30}
                        rowHeight={40}
                        columns={columns}
                        resizable={true}
                        autoPageSize
                        pagination
                        components={{
                            Toolbar: CustomToolbar,
                        }}
                    />
                </div>
            </div>
        )
    }

    return (
        <>
            <TopBarPage />
            <div style={{ display: "flex" }}>
                <LeftBarPage />
                <div id="rmContainer" className="rm-container">
                    <div style={{ width: "100%", overflow: "auto", height: "calc(100vh - 57px)" }}>
                        <div className="row d-flex align-items-center mt-2 ml-3 mb-1 mr-2">
                            <div className="col-lg-6 col-12 m-0 p-0">
                                <p className="h4 text-custom-grey mb-0">Top Movers Reason</p>
                            </div>
                            <div className="col-lg-6 col-12 m-0 p-0 d-flex justify-content-lg-end align-items-center mt-2 mt-lg-0">
                                <label className="mb-0 mr-2 f-12">Select Days</label>
                                <select className="form-control f-10 py-2" style={{ maxWidth: "180px" }} value={days} onChange={(e) => {
                                    setDays(e.target.value)
                                }}>
                                    {DAYS_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        {error ? <p className="text-danger ml-3">{error}</p> : null}
                        {loading ?
                            <div className="loader"></div>
                            :
                            <>
                                {renderTable("Top Gainers", gainers)}
                                {renderTable("Top Losers", loosers)}
                            </>
                        }
                    </div>
                </div>
            </div>
            <ToastPage />

            <Modal open={reasonModal} onClose={handleClose}>
                <div className="modal-fade" tabIndex="-1" role="dialog" aria-hidden="true">
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header border-0">
                                <h5>Add Price Movement Reason</h5>
                                <button type="button" className="close" data-dismiss="modal" aria-label="Close" onClick={handleClose}>
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </div>
                            <div className="modal-body py-3" style={{ overflowY: "auto", maxHeight: "500px" }}>
                                <form className="mr-1">
                                    <div className="form-group">
                                        <label className="f-12">Company Name</label>
                                        <input type="text" className="form-control f-10" disabled value={selectedCompany && selectedCompany.name ? selectedCompany.name : ""} />
                                    </div>
                                    {selectedCompany && selectedCompany.price_change_percentage !== undefined && selectedCompany.price_change_percentage !== null ?
                                        <div className="form-group">
                                            <label className="f-12">Price Change %</label>
                                            <input type="text" className="form-control f-10" disabled value={formatPercent(selectedCompany.price_change_percentage)} />
                                        </div>
                                        : null}
                                    <div className="form-group">
                                        <label className="f-12">{days} Day Price Movement Reason</label>
                                        <textarea className="form-control f-10" rows="3" placeholder={`${days} day price movement reason`} value={reason} onChange={(e) => {
                                            setReason(e.target.value)
                                        }} />
                                    </div>
                                </form>
                                <div className="form-check p-0">
                                    <p className="text-danger">{formError}</p>
                                </div>
                                <button id="addReasonBtn" className="btn btn-primary f-12" onClick={(e) => {
                                    e.preventDefault()
                                    saveReason()
                                }}>Save Reason</button>
                                <div id="addReasonBtnLoader" className="animBtn d-none"><div className="gSignIn-Anim"></div></div>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    )
}
